import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { isEmpty } from 'lodash';
import { FieldChangeGrid } from '@pages/admin/audit-log/change-grid';
import {
    ArrayOfObjectsV2,
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from '@pages/admin/audit-log/service';
import { GET_DATA_REVIEW_TABLES_ALL_COLUMNS } from 'queries/central-monitoring';
import { GET_DATA_LISTINGS_TABLES_FOR_STUDY } from 'queries/subject-monitoring';
import {
    dataReviewTablesAllColumns,
    dataReviewTablesAllColumnsVariables,
} from '@queries/generated/central-monitoring.generated';
import {
    dataListingsTablesForStudy,
    dataListingsTablesForStudyVariables,
} from '@queries/generated/subject-monitoring.generated';

type DataListingColumnAudit = {
    _id?: number;
    ParentListingId?: number;
    Parent?: null;
    FieldKey?: string;
};

type DataListingAuditItem = {
    _id?: number;
    ParentDataId?: number;
    Parent?: null;
    DataListingId?: string;
    Columns?: DataListingColumnAudit[];
};

type ReviewPlanRecord = {
    Active?: boolean;
    ReviewSubject?: boolean;
    DataListings?: DataListingAuditItem[];
    IsDeleted?: boolean;
};

type ReviewPlanChangeGridProps = {
    oldRecord: Partial<ReviewPlanRecord>;
    newRecord: Partial<ReviewPlanRecord>;
    oldItem?: Record<string, unknown>;
    newItem?: Record<string, unknown>;
    // The audit record being rendered. We resolve listing / column names as they were at this
    // record's own timestamp (created) and for its own study, so the log shows point-in-time
    // names rather than whatever the names happen to be now.
    record?: { studyId?: string; created?: string };
};

const SMReviewPlanRecordChangeGrid = ({
    oldRecord,
    newRecord,
    oldItem,
    newItem,
    record,
}: ReviewPlanChangeGridProps) => {
    const studyId = record?.studyId;
    const timestamp = record?.created;

    const { data: listingsData, loading: listingsLoading } = useQuery<
        dataListingsTablesForStudy,
        dataListingsTablesForStudyVariables
    >(GET_DATA_LISTINGS_TABLES_FOR_STUDY, {
        variables: { studyId, timestamp },
        skip: !studyId,
        fetchPolicy: 'no-cache',
    });

    // AllColumns (not the plain Columns field) so a column since removed from its table still
    // resolves to its last known heading here, instead of falling back to its raw field key.
    const { data: tablesData, loading: tablesLoading } = useQuery<
        dataReviewTablesAllColumns,
        dataReviewTablesAllColumnsVariables
    >(GET_DATA_REVIEW_TABLES_ALL_COLUMNS, {
        variables: { studyId, timestamp },
        skip: !studyId,
        fetchPolicy: 'no-cache',
    });

    const dataListingMap = useMemo(() => {
        const map: Record<string, string> = {};
        (listingsData?.dataListingsForStudy ?? []).forEach((dl) => {
            if (dl?.id && dl?.name) map[String(dl.id)] = dl.name;
        });
        return map;
    }, [listingsData]);

    // Map from data-table id => map from field key (`dataField`) to field name (`heading`)
    // Since the field `dataField` values are shared between data-tables, but the field `heading` values are per data-table
    const columnsByKeyByDataTableMap = useMemo(() => {
        const map: Record<string, Record<string, string>> = {};

        (tablesData?.dataReviewTablesForStudy ?? [])
            .filter((table) => table?.id != null)
            .forEach((table) => {
                const tableMap: Record<string, string> = {};

                (table!.allColumns ?? [])
                    .filter((column) => column?.dataField != null && column.heading != null)
                    .forEach((column) => {
                        tableMap[column!.dataField!] = column!.heading!;
                    });

                map[table!.id!] = tableMap;
            });

        return map;
    }, [tablesData]);

    if ((isEmpty(oldRecord) && isEmpty(newRecord)) || listingsLoading || tablesLoading) return null;

    const hiddenFields = ['IsDeleted'];

    const arrayOfObjectsV2: ArrayOfObjectsV2 = {
        DataListings: {
            valueResolver: (recordData: unknown) => {
                if (!recordData) return null;
                const item = recordData as DataListingAuditItem;
                const listingName = dataListingMap[item.DataListingId ?? ''] ?? item.DataListingId ?? 'Unknown';

                // Get the field key-to-name mapping for this specific data-table
                const columnMap =
                    item.DataListingId != null ? columnsByKeyByDataTableMap[item.DataListingId] : undefined;

                const columnNames = (item.Columns ?? [])
                    .map((col) => columnMap?.[col.FieldKey ?? ''] ?? col.FieldKey ?? 'Unknown')
                    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                    .join(', ');
                return columnNames ? `${listingName}: ${columnNames}` : listingName;
            },
            compareField: 'DataListingId',
            valueComparer: (oldObj, newObj) => {
                const getId = (obj: unknown) =>
                    obj instanceof Object && 'DataListingId' in obj
                        ? (obj as DataListingAuditItem).DataListingId
                        : null;
                const getCols = (obj: unknown) =>
                    obj instanceof Object && 'Columns' in obj
                        ? JSON.stringify(
                              [...((obj as DataListingAuditItem).Columns ?? [])].map((c) => c.FieldKey).sort(),
                          )
                        : '';
                return getId(oldObj) !== getId(newObj) || getCols(oldObj) !== getCols(newObj);
            },
        },
    };

    // Build both arrays together so each index always corresponds to the same DataListingId.
    // Without alignment, the service's positional `editedValueIndex === i` guard fires incorrectly
    // whenever a listing is added or removed (shifting sibling indices), producing spurious rows.
    const alignDataListings = (
        oldListings: DataListingAuditItem[] | undefined,
        newListings: DataListingAuditItem[] | undefined,
    ): { alignedOld: (DataListingAuditItem | null)[]; alignedNew: (DataListingAuditItem | null)[] } => {
        const oldMap = new Map(
            (oldListings ?? []).filter((x): x is DataListingAuditItem => x != null).map((x) => [x.DataListingId, x]),
        );
        const newMap = new Map(
            (newListings ?? []).filter((x): x is DataListingAuditItem => x != null).map((x) => [x.DataListingId, x]),
        );
        const allIds = Array.from(new Set([...oldMap.keys(), ...newMap.keys()])).sort((a, b) => {
            const nameA = dataListingMap[a ?? ''] ?? a ?? '';
            const nameB = dataListingMap[b ?? ''] ?? b ?? '';
            return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
        });
        return {
            alignedOld: allIds.map((id) => oldMap.get(id) ?? null),
            alignedNew: allIds.map((id) => newMap.get(id) ?? null),
        };
    };

    const { alignedOld, alignedNew } = alignDataListings(oldRecord.DataListings, newRecord.DataListings);

    const refactoredOldRecord = {
        ...oldRecord,
        ...removePropertiesFromObject(oldRecord, hiddenFields),
        DataListings: alignedOld,
    } as Record<string, unknown>;

    const refactoredNewRecord = {
        ...newRecord,
        ...removePropertiesFromObject(newRecord, hiddenFields),
        DataListings: alignedNew,
    } as Record<string, unknown>;

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            arrayOfObjectsV2,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { SMReviewPlanRecordChangeGrid };
